import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  Form,
  Input,
  message,
  Modal,
  Row,
  Col,
  Select,
  Tooltip,
  Upload,
  Typography,
  Tag
} from "antd";
import { CloseOutlined, EyeOutlined, UploadOutlined } from "@ant-design/icons";
import api from "../../../api";
import SuccessModal from "../Events/SuccessModal";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

function CreateForum({ visible, onClose, onSuccess, editdata }) {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [bannerPreview, setBannerPreview] = useState(null);
  const [isBannerChanged, setIsBannerChanged] = useState(false);
  const [bannerModalVisible, setBannerModalVisible] = useState(false);
  const [bannerFileList, setBannerFileList] = useState([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [availableSponsors, setAvailableSponsors] = useState([]);

  const fetchSponsors = async () => {
    try {
      const requestData = {
        synceddatetime: dayjs().format("YYYY-MM-DD HH:mm:ss"),
        FormCode: "201",
        AppVersion: "1.0.0.0",
        ApiKey: "kavin",
        AppTypeNo: "3",
      };

      const res = await api.getSponsorCreation(requestData);


      if (res?.data?.status === "1" && Array.isArray(res.data.sponsor_data)) {
        setAvailableSponsors(res.data.sponsor_data);
      } else {
        messageApi.warning("No sponsors available or failed to fetch sponsors");
      }
    } catch (err) {
      console.error("Error fetching sponsors:", err);
      messageApi.error("Failed to fetch sponsors");
    }
  };

  useEffect(() => {
    fetchSponsors()
  }, [])

  useEffect(() => {
    if (editdata && availableSponsors.length > 0) {


      // Prefill normal fields
      form.setFieldsValue({
        title: editdata.fld_forum_title,
        description: editdata.fld_forum_description,
        categoryName: editdata.fld_category_name,
        categoryDescription: editdata.fld_category_description,
        threadType: editdata.fld_thread_type_name,
        threadStatus: editdata.fld_threads_status_name,
        announcementTitle: editdata.fld_announcement_title,
        announcementMessage: editdata.fld_announcement_message,
      });

      // ⭐ FIXED: Correct Sponsor Mapping from sponsors_data array
      if (Array.isArray(editdata.sponsors_data) && editdata.sponsors_data.length > 0) {
        // Extract sponsor IDs by matching with available sponsors
        const selectedIds = editdata.sponsors_data
          .map(sponsor => {
            // Find matching sponsor in availableSponsors by name and logo
            const matchedSponsor = availableSponsors.find(avail =>
              avail.fld_sponsor_name === sponsor.fld_sponsors_name &&
              avail.fld_logo_path === sponsor.fld_sponsors_loge
            );
            return matchedSponsor ? matchedSponsor.fld_sponsor_id : null;
          })
          .filter(id => id !== null); // Remove null values



        // Set sponsor IDs in form
        form.setFieldsValue({
          sponsorIds: selectedIds,
        });
      } else {
        // No sponsors in edit data
        form.setFieldsValue({
          sponsorIds: [],
        });
      }

      // Prefill banner logic
      if (editdata.fld_cover_image_url) {
        setBannerPreview(editdata.fld_cover_image_url);
        setBannerFileList([
          {
            uid: "-1",
            name: "cover_image.png",
            status: "done",
            url: editdata.fld_cover_image_url,
          },
        ]);
        setIsBannerChanged(false);
        form.setFieldsValue({ bannerImage: editdata.fld_cover_image_url });
      }
    } else {
      // Reset if not editing
      form.resetFields();
      setBannerPreview(null);
      setBannerFileList([]);
    }
  }, [editdata, availableSponsors, form]);

  const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const handleCloseModal = () => {
    setShowSuccessModal(false);
    onClose();
  };

  const handleRemoveBanner = () => {
    setBannerPreview(null);
    setBannerFileList([]);
    setIsBannerChanged(false);
    form.setFieldsValue({ bannerImage: null })
  };

  const validateImage = (file) => {
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];

    const isValidType = allowedTypes.includes(file.type);

    if (!isValidType) {
      messageApi.warning(
        "Only JPG, JPEG and PNG images are allowed."
      );
      return Upload.LIST_IGNORE;
    }

    const isLessThan5MB = file.size / 1024 / 1024 < 5;

    if (!isLessThan5MB) {
      messageApi.warning(
        "Image size must be less than 5 MB."
      );
      return Upload.LIST_IGNORE;
    }

    return true;
  };

  const validateImageFormat = (file) => {
    const allowedFormats = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    const isValid = allowedFormats.includes(file.type);
    if (!isValid) messageApi.error("Only JPG, JPEG, PNG, or WebP allowed!");
    return isValid;
  };

  const handleBannerChange = ({ fileList }) => {
    const file = fileList[0];
    if (file && validateImageFormat(file)) {
      getBase64(file.originFileObj).then(setBannerPreview);
      setBannerFileList([file]);
      setIsBannerChanged(true);
    } else {
      setBannerFileList([]);
      setBannerPreview(null);
      setIsBannerChanged(false);
    }
  };

  const handleFinish = async (values) => {
    try {
      const formData = new FormData();

      // Add rf_id only when editing
      formData.append("fld_rf_id", editdata?.fld_rf_id || "");

      // Common required fields
      formData.append("fld_forum_id", editdata?.fld_forum_id || "");
      formData.append("fld_forum_title", values.title);
      formData.append("fld_forum_description", values.description);

      // Banner / Cover Image logic
      if (isBannerChanged && bannerFileList[0]?.originFileObj) {
        formData.append("fld_cover_image_url", bannerFileList[0].originFileObj);
      } else if (!isBannerChanged && editdata?.fld_cover_image_url) {
        formData.append("fld_cover_image_url", editdata.fld_cover_image_url);
      } else {
        messageApi.error("Please upload a banner image!");
        return;
      }

      // Category fields
      formData.append("fld_category_name_id", "");
      formData.append("fld_category_id", "");
      formData.append("fld_category_name", values.categoryName);
      formData.append("fld_category_description", values.categoryDescription);

      // Thread details
      formData.append("fld_thread_type_id", "");
      formData.append("fld_thread_type_name", values.threadType);
      formData.append("fld_threads_status_id", "");
      formData.append("fld_threads_status_name", values.threadStatus);

      // Announcement details
      formData.append("fld_announcement_title", values.announcementTitle || "");
      formData.append("fld_announcement_message", values.announcementMessage || "");

      // Sponsored / extra details
      formData.append("fld_is_sponsored_id", "");
      formData.append("fld_is_sponsored_name", "");
      formData.append("fld_sponcer_name", "");
      formData.append("fld_sponsor_link_url", "");
      formData.append("fld_sponsor_badge_text", "");

      // Geo and meta info
      formData.append("fld_geo_x", "");
      formData.append("fld_geo_y", "");
      formData.append("fld_is_full_form_completed_form", "");
      formData.append("fld_form_start_time", "");
      formData.append("fld_form_end_time", "");
      formData.append("fld_loggedin_user_id", "admin");
      formData.append("data_source", "w");
      formData.append("fld_app_version", "11");

      // ⭐ FIXED: Add Sponsors with proper mapping
      if (values.sponsorIds && values.sponsorIds.length > 0) {
        values.sponsorIds.forEach((id) => {
          const sponsor = availableSponsors.find(s => s.fld_sponsor_id === id);

          if (sponsor) {
            // Sponsor Name
            formData.append("fld_child_rf_id[]", sponsor.fld_child_rf_id || "");
            formData.append("fld_sponsors_name[]", sponsor.fld_sponsor_name);

            // Sponsor Website
            formData.append("fld_website_url[]", sponsor.fld_url || "");

            // Sponsor Logo - send URL (backend will handle it)
            formData.append("fld_sponsors_loge[]", sponsor.fld_logo_path || "");
          }
        });
      } else {
        // No sponsors selected
        formData.append("fld_sponsors_name[]", "");
        formData.append("fld_sponsors_loge[]", "");
        formData.append("fld_website_url[]", "");
        formData.append("fld_child_rf_id[]", "");
      }


      // Send to API
      const response = await api.forumCreation(formData);

      if (response?.data?.status === "1") {
        setShowSuccessModal(true)
        onSuccess?.(formData);
      } else {
        messageApi.error(response?.data?.message || "Failed to create forum");
      }
    } catch (error) {
      console.error("❌ Forum creation error:", error);
      messageApi.error("An error occurred while submitting the form");
    }
  };

  return (
    <>
      {contextHolder}
      <Modal
        open={visible}
        onCancel={onClose}
        footer={null}
        width="100%"
        style={{
          maxWidth: 1100,
          top: 40,
          overflow: "hidden",
        }}
        bodyStyle={{
          overflowX: "hidden",
          paddingRight: 24,
        }}
        className="event-form-modal"
        closeIcon={<CloseOutlined style={{ fontSize: "18px" }} />}
      >
        <Card className="event-form-card">
          <Title level={3}>{editdata ? "Edit Forum" : "Create New Forum"}</Title>
          <Text>
            {editdata
              ? "Modify the details below to update the forum."
              : "Fill in the details to create a new forum."}
          </Text>

          <Form
            layout="vertical"
            form={form}
            onFinish={handleFinish}
            className="event-form"
          >
            {/* Forum Title */}
            <Form.Item
              name="title"
              label="Forum Title"
              rules={[{ required: true }]}
              help="Enter the name of your forum. Example: Tech Talk"
            >
              <Input placeholder="Enter forum title" />
            </Form.Item>

            {/* Forum Description */}
            <Form.Item
              name="description"
              label="Forum Description"
              rules={[{ required: true }]}
              help="Provide a short summary of the forum topic."
            >
              <TextArea rows={4} placeholder="Enter forum description" />
            </Form.Item>

            {/* Cover Image */}
            <Form.Item
              name="bannerImage"
              label="Cover Image / Banner"
              rules={[{ required: true, message: "Please upload a banner image!" }]}
              help="Upload a banner image to represent your forum. JPG or PNG only."
              required
            >

              <div
                style={{
                  height: 200,
                  border: "1px dashed #d9d9d9",
                  borderRadius: 6,
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                {bannerPreview ? (
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      overflow: "hidden",
                      cursor: "pointer",
                      position: "relative",
                    }}
                  >
                    <img
                      src={bannerPreview}
                      alt="Banner Preview"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        display: "flex",
                        gap: 8,
                        zIndex: 100,
                        pointerEvents: "auto",
                      }}
                    >
                      <Tooltip title="Preview banner">
                        <Button
                          type="primary"
                          shape="circle"
                          icon={<EyeOutlined />}
                          style={{
                            height: 32,
                            width: 32,
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setBannerModalVisible(true);
                          }}
                        />
                      </Tooltip>
                      <Tooltip title="Remove banner">
                        <Button
                          type="primary"
                          shape="circle"
                          icon={<CloseOutlined />}
                          danger
                          style={{
                            height: 32,
                            width: 32,
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveBanner();
                          }}
                        />
                      </Tooltip>
                    </div>
                  </div>
                ) : (
                  <Upload.Dragger
                    name="bannerImage"
                    maxCount={1}
                    showUploadList={false}
                    beforeUpload={(file) => {
                      const isValid = validateImage(file);

                      if (isValid !== true) {
                        setBannerFileList([]);
                        setBannerPreview(null);
                        return Upload.LIST_IGNORE;
                      }

                      getBase64(file).then((base64) => {
                        setBannerPreview(base64); // show preview immediately
                      });

                      setBannerFileList([
                        {
                          uid: file.uid,
                          name: file.name,
                          originFileObj: file,
                        },
                      ]);

                      setIsBannerChanged(true);
                      form.setFieldsValue({ bannerImage: file });

                      return false; // prevent auto upload
                    }}
                    fileList={bannerFileList}
                  >
                    <div style={{ textAlign: "center" }}>
                      <UploadOutlined style={{ fontSize: 24, color: "#1980B9" }} />
                      <div style={{ marginTop: 8 }}>Click or drag file to upload</div>
                    </div>
                  </Upload.Dragger>
                )}
              </div>
            </Form.Item>

            <Row gutter={24}>
              {/* Category Name */}
              <Col xs={24} md={12}>
                <Form.Item
                  name="categoryName"
                  label="Category Name"
                  rules={[{ required: true }]}
                  help="Select or enter the category for this forum."
                >
                  <Select placeholder="Select a category">
                    <Option value="Education">Education</Option>
                    <Option value="Technology">Technology</Option>
                    <Option value="Science">Science</Option>
                  </Select>
                </Form.Item>
              </Col>

              {/* Category Description */}
              <Col xs={24} md={12}>
                <Form.Item
                  name="categoryDescription"
                  label="Category Description"
                  rules={[{ required: true }]}
                  help="Short description of the chosen category."
                >
                  <Input placeholder="Short description about category" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              {/* Thread Type */}
              <Col xs={24} md={12}>
                <Form.Item
                  name="threadType"
                  label="Thread Type"
                  rules={[{ required: true }]}
                  help="Choose whether the forum is public or private."
                >
                  <Select placeholder="Select thread type">
                    <Option value="Public">Public</Option>
                    <Option value="Private">Private</Option>
                  </Select>
                </Form.Item>
              </Col>

              {/* Thread Status */}
              <Col xs={24} md={12}>
                <Form.Item
                  name="threadStatus"
                  label="Threads Status"
                  rules={[{ required: true }]}
                  help="Set the current operational status of the forum."
                >
                  <Select placeholder="Select status">
                    <Option value="Active">Active</Option>
                    <Option value="Inactive">Inactive</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            {/* Announcement Section */}
            <Form.Item label="Announcement (Optional)">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="announcementTitle"
                    label="Announcement Title"
                    help="Enter the Announcement Title"
                    className="no-label-margin"
                  >
                    <TextArea rows={2} placeholder="Enter title" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="announcementMessage"
                    label="Announcement Message"
                    help="Enter the Announcement Message"
                    className="no-label-margin"
                  >
                    <TextArea rows={2} placeholder="Enter message" />
                  </Form.Item>
                </Col>
              </Row>
            </Form.Item>

            <Form.Item
              name="sponsorIds"
              label="Select Sponsors"
              help="Choose one or more sponsors for this forum."
              rules={[{ required: false }]}
            >
              <Select
                mode="multiple"
                placeholder="Select sponsors"
                optionFilterProp="label"
                value={form.getFieldValue('sponsorIds')}
                tagRender={({ label, value, onClose }) => {
                  const sponsor = availableSponsors.find(s => s.fld_sponsor_id === value);
                  return (
                    <Tag closable onClose={onClose} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      {sponsor && (
                        <>
                          <img
                            src={sponsor.fld_logo_path}
                            alt={sponsor.fld_sponsor_name}
                            style={{ width: 16, height: 16, borderRadius: 2 }}
                          />
                          {sponsor.fld_sponsor_name}
                        </>
                      )}
                    </Tag>
                  );
                }}
              >
                {availableSponsors.map((sponsor) => (
                  <Select.Option
                    key={sponsor.fld_sponsor_id}
                    value={sponsor.fld_sponsor_id}
                    label={sponsor.fld_sponsor_name}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <img
                        src={sponsor.fld_logo_path}
                        alt={sponsor.fld_sponsor_name}
                        style={{ width: 24, height: 24, borderRadius: 4 }}
                      />
                      {sponsor.fld_sponsor_name}
                    </div>
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item>
              <Row justify="center">
                <Button
                  type="primary"
                  htmlType="submit"
                  style={{
                    backgroundColor: "#1980B9",
                    borderColor: "#1980B9",
                    width: "20%",
                    height: 40,
                    marginTop: 30,
                    marginRight: 15,
                    fontSize: 16,
                  }}
                >
                  {editdata ? "Update Forum" : "Create Forum"}
                </Button>
              </Row>
            </Form.Item>
          </Form>
        </Card>
      </Modal>

      {/* Image Preview Modal */}
      <Modal
        open={bannerModalVisible}
        footer={null}
        onCancel={() => setBannerModalVisible(false)}
      >
        <img src={bannerPreview} alt="Banner" style={{ width: "100%" }} />
      </Modal>

      {showSuccessModal === true &&
        <SuccessModal
          visible={showSuccessModal}
          onClose={handleCloseModal}
          title={editdata ? "Forum data Updated " : "Forum Created successfully"}
          description={editdata ? "Your Forum has successfully Updated and saved to the system." : "Your Forum has been successfully created and saved to the system."}
        />
      }
    </>
  );
}

export default CreateForum;